/**
 * `<caos-kanban-board>` — the BOARD body of a list surface.
 * (Catalog: Data display › KanbanBoard — the BOARD body of a list surface.)
 *
 * The same records a table would show, stood up in columns by the one field that says what STAGE a
 * record is at. It answers a question a table cannot: not "what is in this list" but "where is the
 * work". A column exists for every value the field can take — including the ones nothing is sitting
 * in, because an empty stage is information ("nothing is waiting on approval") and a column that
 * vanishes when it empties makes the board a different shape every time you look at it.
 *
 * Records whose stage is blank collect in a leading "Unset" column rather than being dropped; a
 * record missing from a board it belongs on is the worst outcome here.
 *
 * Data in, events out — the contract `<caos-data-table>` and `<caos-card-grid>` follow. The app
 * supplies `columns` (already ordered, labelled and FLS-filtered) and the board emits
 * `caos-card-open` (`{ id }`) when a card is activated, which the host routes through the app's
 * navigation. Cards are NOT draggable: moving one would be a WRITE, and this component is a way of
 * reading. When dragging arrives it arrives as an explicit, permission-checked action.
 *
 * Styling seams: `::part(board)`, `::part(column)`, `::part(card)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';

/** One card on the board — a record: its name, an optional sub-line, and where it opens. */
export interface CaosKanbanCard {
  id: string;
  title: string;
  href?: string;
  /** The quiet line under the name (e.g. the account it belongs to, or an amount). */
  subtitle?: string;
}

/** One column — a value the stage field can take, and the records currently sitting at it. */
export interface CaosKanbanColumn {
  /** The stored value this column stands for (`''` for the unset column). */
  value: string;
  /** The value's label, as the value set words it. */
  label: string;
  /** The value's intent, when it carries one — colours the column's marker only. */
  intent?: string;
  cards: CaosKanbanCard[];
}

const sheet = makeSheet(`
  :host { display: block; }

  /* CAOS-652 — padding, gaps and radii come from the spacing and radius scales. The pixel figures
     left in this sheet are intrinsic sizes (the column width and minimum height, the intent mark),
     hairline borders and type sizes. */

  /* The board scrolls SIDEWAYS as one, so the columns keep a readable width instead of being
     squeezed to nothing when there are many stages. The page itself must never scroll sideways. */
  .board {
    display: flex;
    gap: var(--caos-space-4);
    align-items: flex-start;
    overflow-x: auto;
    padding-bottom: var(--caos-space-2);
    font-family: var(--caos-font);
  }

  .column {
    flex: 0 0 268px;
    display: flex;
    flex-direction: column;
    gap: var(--caos-space-2);
    padding: var(--caos-space-3);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius);
    background: var(--caos-color-surface);
    min-height: 120px;
  }

  .head {
    display: flex;
    align-items: center;
    gap: var(--caos-space-2);
    min-width: 0;
  }
  .mark {
    flex: none;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--caos-color-text-muted);
  }
  /* CAOS-652 — the intent tokens themselves. The fallbacks these carried matched none of the tokens'
     own values in any brand or theme, which made them a second palette rather than a safety net. */
  .mark[data-intent='success'] { background: var(--caos-color-success); }
  .mark[data-intent='warning'] { background: var(--caos-color-warning); }
  .mark[data-intent='danger'] { background: var(--caos-color-danger); }
  .mark[data-intent='info'] { background: var(--caos-color-accent); }

  .name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--caos-color-text-muted);
  }
  .tally {
    flex: none;
    font-size: 11px;
    color: var(--caos-color-text-muted);
    font-variant-numeric: tabular-nums;
  }

  .card {
    display: block;
    width: 100%;
    text-align: left;
    padding: var(--caos-space-3);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-bg);
    color: inherit;
    font: inherit;
    cursor: pointer;
  }
  .card:hover { border-color: var(--caos-color-accent); }
  .card:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  .card-title {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    font-weight: 600;
    color: var(--caos-color-accent);
  }
  .card-sub {
    display: block;
    margin-top: var(--caos-space-1);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 11px;
    color: var(--caos-color-text-muted);
  }

  .none {
    margin: 0;
    font-size: 11px;
    color: var(--caos-color-text-muted);
  }
`);

export class CaosKanbanBoard extends CaosElement {
  private _columns: readonly CaosKanbanColumn[] = [];

  constructor() {
    super(sheet);
  }

  connectedCallback(): void {
    this.render();
  }

  set columns(next: readonly CaosKanbanColumn[]) {
    this._columns = Array.isArray(next) ? next : [];
    this.render();
  }
  get columns(): readonly CaosKanbanColumn[] {
    return this._columns;
  }

  private render(): void {
    const board = document.createElement('div');
    board.className = 'board';
    board.setAttribute('part', 'board');

    for (const col of this._columns) {
      const column = document.createElement('div');
      column.className = 'column';
      column.setAttribute('part', 'column');

      const head = document.createElement('div');
      head.className = 'head';
      const mark = document.createElement('span');
      mark.className = 'mark';
      if (col.intent) mark.dataset.intent = col.intent;
      const name = document.createElement('span');
      name.className = 'name';
      name.textContent = col.label;
      name.title = col.label;
      const tally = document.createElement('span');
      tally.className = 'tally';
      tally.textContent = String(col.cards.length);
      head.append(mark, name, tally);
      column.append(head);

      if (col.cards.length === 0) {
        const none = document.createElement('p');
        none.className = 'none';
        // Said plainly, because an empty stage is an answer, not a gap.
        none.textContent = 'Nothing here';
        column.append(none);
      }

      for (const card of col.cards) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'card';
        button.setAttribute('part', 'card');
        const title = document.createElement('span');
        title.className = 'card-title';
        title.textContent = card.title;
        button.append(title);
        if (card.subtitle) {
          const sub = document.createElement('span');
          sub.className = 'card-sub';
          sub.textContent = card.subtitle;
          button.append(sub);
        }
        button.title = card.subtitle ? `${card.title} — ${card.subtitle}` : card.title;
        button.addEventListener('click', () => {
          this.dispatchEvent(
            new CustomEvent('caos-card-open', { detail: { id: card.id }, bubbles: true, composed: true }),
          );
        });
        column.append(button);
      }

      board.append(column);
    }

    this.root.replaceChildren(board);
  }
}

define('caos-kanban-board', CaosKanbanBoard);

declare global {
  interface HTMLElementTagNameMap {
    'caos-kanban-board': CaosKanbanBoard;
  }
}
